// Accounts — the dense fleet table. Sortable / filterable / resizable / hideable columns,
// density modes, quick + select filters, saved views (localStorage), search, frozen
// Account + Conn columns, live cell-flash, row → inspector drawer. Narrow layout renders
// prioritized cards + a bottom-sheet filter. Ported from the mockup `rvAccounts`.

import { useMemo, type CSSProperties } from 'react';
import { C, MONO } from '../lib/tokens';
import { fmtAge, fmtMoney, fmtPctStr, hbTitle } from '../lib/format';
import { CHIP_BASE, CHIP_ON, chipS } from '../lib/styles';
import { derive, type Derived, type Thresholds } from '../lib/mappers';
import type { AccountRow } from '../types/api';
import { useData, useServerNow, useUi } from '../store/hooks';
import {
  clearFilters,
  openDrawer,
  saveView,
  setColWidth,
  setSort,
  toggleColumn,
  uiStore,
  type SelectFilters,
} from '../store/uiStore';
import { COLUMNS, SORT_DEFAULT, sortAccessor } from './accountsColumns';
import { AccountFilters, QUICK } from '../components/accountFilters';

/** Flash key → account field mapping for cell highlight. */
const FLASH_FIELD: Record<string, string> = { eq: 'equity', fpl: 'floating_pl', dpl: 'daily_pl', qt: 'quote_at', bal: 'balance' };

export function Accounts() {
  const accounts = useData((s) => s.accounts);
  const flash = useData((s) => s.flash);
  const thr = useUi((s) => s.thr);
  const q = useUi((s) => s.q);
  const quick = useUi((s) => s.quick);
  const f = useUi((s) => s.f);
  const sort = useUi((s) => s.sort);
  const colW = useUi((s) => s.colW);
  const hidden = useUi((s) => s.hidden);
  const dens = useUi((s) => s.dens);
  const narrow = useUi((s) => s.narrow);
  const saveOpen = useUi((s) => s.saveOpen);
  const viewName = useUi((s) => s.viewName);
  const colMenu = useUi((s) => s.colMenu);
  const serverNow = useServerNow();
  const nowLocal = Date.now();

  const derivedMap = useMemo(() => {
    const m = new Map<string, Derived>();
    for (const a of accounts) m.set(a.id, derive(a, serverNow, thr));
    return m;
  }, [accounts, serverNow, thr]);

  const recentIds = useMemo(() => {
    const s = new Set<string>();
    for (const key in flash) {
      if (nowLocal - flash[key]! < 300000) s.add(key.split(':')[0]!);
    }
    return s;
  }, [flash, nowLocal]);

  const qNorm = q.trim().toLowerCase();
  const qMatch = (a: AccountRow) =>
    !qNorm ||
    (a.label + ' ' + a.login_masked + ' ' + a.broker + ' ' + a.server + ' ' + a.vps_id + ' ' + a.terminal_id + ' ' + a.strategy + ' ' + a.tags.join(' ') + ' ' + a.magic)
      .toLowerCase()
      .includes(qNorm);

  const selMatch = (a: AccountRow, d: Derived) =>
    (!f.vps || a.vps_id === f.vps) &&
    (!f.broker || a.broker === f.broker) &&
    (!f.ccy || a.currency === f.ccy) &&
    (!f.risk || d.risk === f.risk) &&
    (!f.conn || d.conn === f.conn) &&
    (!f.strat || a.strategy === f.strat);

  const list = useMemo(() => {
    const out = accounts.filter((a) => {
      const d = derivedMap.get(a.id)!;
      if (!qMatch(a) || !selMatch(a, d)) return false;
      for (const k in quick) {
        if (quick[k] && QUICK[k] && !QUICK[k]!.pred(a, d, thr, recentIds.has(a.id))) return false;
      }
      return true;
    });
    const sk = sort.k;
    const sd = sort.d;
    if (sk === 'sev') {
      out.sort((p, r) => {
        const dp = derivedMap.get(p.id)!;
        const dr = derivedMap.get(r.id)!;
        return dp.rank - dr.rank || (dr.eqNum ?? -Infinity) - (dp.eqNum ?? -Infinity);
      });
    } else {
      out.sort((p, r) => {
        const av = sortAccessor(sk, p, derivedMap.get(p.id)!);
        const bv = sortAccessor(sk, r, derivedMap.get(r.id)!);
        return (av < bv ? -1 : av > bv ? 1 : 0) * sd;
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, derivedMap, quick, f, qNorm, sort, thr, recentIds]);

  const visibleCols = COLUMNS.filter((c) => !hidden[c.id]);
  const widthOf = (id: string) => colW[id] ?? COLUMNS.find((c) => c.id === id)!.w;

  // Frozen (sticky) left offsets for acct + conn.
  const frozenLeft: Record<string, number> = {};
  let off = 0;
  for (const c of visibleCols) {
    if (c.id === 'acct' || c.id === 'conn') {
      frozenLeft[c.id] = off;
      off += widthOf(c.id);
    }
  }
  const tableW = visibleCols.reduce((s, c) => s + widthOf(c.id), 0) + 2;
  const rowH = dens === 'compact' ? 36 : 44;

  function startRz(id: string) {
    return (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const w0 = widthOf(id);
      const move = (ev: PointerEvent) => setColWidth(id, w0 + ev.clientX - startX);
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    };
  }

  const resultNote =
    list.length +
    ' of ' +
    accounts.length +
    ' accounts · sort: ' +
    (sort.k === 'sev'
      ? 'priority (critical → offline → warning)'
      : (COLUMNS.find((c) => c.id === sort.k)?.label ?? '') + (sort.d === 1 ? ' ↑' : ' ↓')) +
    ' · click a row to inspect';

  const clearShow = !!(qNorm || Object.keys(quick).length || Object.keys(f).some((k) => f[k as keyof SelectFilters]));

  return (
    <section data-screen-label="Accounts" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', position: 'relative' }}>
        <h1 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Accounts</h1>
        <span style={{ fontSize: 11, color: C.dm, fontFamily: MONO }}>{resultNote}</span>
        <div style={{ flex: 1 }} />
        {narrow && (
          <button onClick={() => uiStore.set((s) => ({ sheetOpen: !s.sheetOpen }))} style={{ ...CHIP_BASE, fontSize: 11.5 }}>
            Filters ▾
          </button>
        )}
        <div style={{ display: 'flex', gap: 4 }} role="group" aria-label="Row density">
          {(['compact', 'comfortable'] as const).map((v) => (
            <button key={v} onClick={() => uiStore.set({ dens: v })} aria-pressed={dens === v} style={{ ...(dens === v ? CHIP_ON : CHIP_BASE), padding: '3px 9px', fontSize: 10.5 }}>
              {v === 'compact' ? 'Compact' : 'Comfy'}
            </button>
          ))}
        </div>
        <button onClick={() => uiStore.set((s) => ({ colMenu: !s.colMenu }))} style={{ ...CHIP_BASE, fontSize: 10.5, padding: '3px 9px' }}>
          Columns ▾
        </button>
        {colMenu && (
          <div style={{ position: 'absolute', top: 32, right: 0, zIndex: 60, background: '#111A2B', border: '1px solid ' + C.ln, borderRadius: 8, padding: '10px 12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', boxShadow: '0 18px 44px rgba(0,0,0,.5)' }}>
            {COLUMNS.filter((c) => c.id !== 'acct').map((c) => (
              <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: C.b9, whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={!hidden[c.id]} onChange={() => toggleColumn(c.id)} />
                {c.label}
              </label>
            ))}
          </div>
        )}
      </div>

      {!narrow && <AccountFilters />}

      {!narrow && (
        <div style={{ border: '1px solid ' + C.ln, borderRadius: 8, background: C.s1, overflow: 'auto', maxHeight: 'calc(100vh - 240px)' }}>
          <table aria-label="Accounts" style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', minWidth: '100%', width: tableW }}>
            <thead>
              <tr>
                {visibleCols.map((c) => {
                  const isFrozen = c.id === 'acct' || c.id === 'conn';
                  const isSorted = sort.k === c.id;
                  const w = widthOf(c.id);
                  const th: CSSProperties = {
                    position: 'sticky',
                    top: 0,
                    left: isFrozen ? frozenLeft[c.id] : undefined,
                    zIndex: isFrozen ? 7 : 5,
                    background: '#0E1524',
                    borderBottom: '1px solid ' + C.ln,
                    borderRight: c.id === 'conn' ? '1px solid ' + C.ln : undefined,
                    width: w,
                    minWidth: w,
                    maxWidth: w,
                    padding: 0,
                    height: 34,
                  };
                  return (
                    <th key={c.id} scope="col" aria-sort={isSorted ? (sort.d === 1 ? 'ascending' : 'descending') : 'none'} style={th}>
                      <button
                        onClick={() => setSort(c.id, SORT_DEFAULT[c.id]!)}
                        title={'Sort by ' + c.label}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          width: '100%',
                          height: 34,
                          justifyContent: c.al === 'right' ? 'flex-end' : 'flex-start',
                          background: 'none',
                          border: 'none',
                          color: isSorted ? C.tx : C.dm,
                          fontSize: 10,
                          fontWeight: 600,
                          letterSpacing: '.04em',
                          textTransform: 'uppercase',
                          padding: '0 10px',
                          fontFamily: 'inherit',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.label}</span>
                        <span aria-hidden="true" style={{ fontSize: 8 }}>{isSorted ? (sort.d === 1 ? '▲' : '▼') : ''}</span>
                      </button>
                      <span onPointerDown={startRz(c.id)} aria-hidden="true" style={{ position: 'absolute', top: 0, right: -3, width: 7, height: '100%', cursor: 'col-resize', zIndex: 3 }} />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {list.map((a) => {
                const d = derivedMap.get(a.id)!;
                const crit = d.risk === 'critical';
                const dim = (d.conn === 'stale' || d.conn === 'offline') && !crit;
                return (
                  <tr
                    key={a.id}
                    tabIndex={0}
                    onClick={() => openDrawer(a.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') openDrawer(a.id);
                    }}
                    aria-label={`${a.label}, connection ${d.cm[2]}, broker ${d.bm[2]}, risk ${d.rm[2]}, equity ${fmtMoney(a.equity, a.currency)}`}
                    style={{ cursor: 'pointer', opacity: dim ? 0.72 : 1 }}
                  >
                    {visibleCols.map((c) => (
                      <Cell key={c.id} col={c} a={a} d={d} crit={crit} rowH={rowH} frozenLeft={frozenLeft} flashTs={flash[`${a.id}:${FLASH_FIELD[c.id] ?? ''}`]} nowLocal={nowLocal} thr={thr} />
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {list.length === 0 && (
            <div style={{ padding: '44px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>{accounts.length === 0 ? 'No accounts connected' : 'No accounts match the current filters'}</div>
              <div style={{ fontSize: 12, color: C.dm, marginTop: 6 }}>
                {accounts.length === 0
                  ? 'The collector is reachable but no terminals are registered. Check the EA installation on the VPS.'
                  : 'Clear filters or adjust the search to see the fleet.'}
              </div>
            </div>
          )}
        </div>
      )}

      {narrow && <Cards list={list} derivedMap={derivedMap} thr={thr} />}

      {clearShow && !narrow && (
        <div>
          <button onClick={clearFilters} style={{ background: 'none', border: 'none', color: C.dm, fontSize: 11.5, padding: 0, textDecoration: 'underline' }}>
            Clear all filters
          </button>
        </div>
      )}

      {/* Saved-view controls live in the Filters block; keep the save affordance reachable. */}
      {saveOpen && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            value={viewName}
            onChange={(e) => uiStore.set({ viewName: e.target.value })}
            placeholder="View name…"
            aria-label="View name"
            style={{ height: 26, background: '#0A0F19', border: '1px solid ' + C.ln, borderRadius: 5, color: C.tx, fontSize: 11.5, padding: '0 8px', width: 130 }}
          />
          <button onClick={saveView} style={{ height: 26, padding: '0 10px', background: C.bl, border: 'none', borderRadius: 5, color: '#0B1220', fontSize: 11, fontWeight: 600 }}>
            Save
          </button>
        </div>
      )}
    </section>
  );
}

// ---- Individual table cell ------------------------------------------------------------

function Cell({
  col,
  a,
  d,
  crit,
  rowH,
  frozenLeft,
  flashTs,
  nowLocal,
  thr,
}: {
  col: { id: string; al: 'left' | 'right' };
  a: AccountRow;
  d: Derived;
  crit: boolean;
  rowH: number;
  frozenLeft: Record<string, number>;
  flashTs: number | undefined;
  nowLocal: number;
  thr: Thresholds;
}) {
  let t1 = '';
  let t2 = '';
  let ti = '';
  let color: string = C.tx;
  let mono1 = false;
  let bold = false;

  switch (col.id) {
    case 'acct':
      t1 = a.label;
      bold = true;
      t2 = a.login_masked + ' · ' + a.currency;
      ti = a.label + ' · ' + a.broker + ' ' + a.server;
      break;
    case 'conn':
      mono1 = true;
      color = d.cm[1];
      t1 = d.cm[0] + ' ' + d.cm[2];
      t2 = fmtAge(d.hbAgeMs);
      ti = hbTitle(d.receivedAtMs, a.broker_tz_offset);
      break;
    case 'bconn':
      mono1 = true;
      color = d.bm[1];
      t1 = d.bm[0] + ' ' + d.bm[2];
      ti = 'Terminal ↔ broker connection';
      break;
    case 'risk':
      mono1 = true;
      color = d.rm[1];
      t1 = d.rm[0] + ' ' + d.rm[2];
      t2 = d.why;
      ti = 'Risk: ' + (d.why || 'within thresholds');
      break;
    case 'trade':
      mono1 = true;
      color = d.tm[1];
      t1 = d.tr === 'open' ? '▸ ' + a.positions_count + ' open' : d.tm[0] + ' ' + d.tm[2];
      t2 = d.tr === 'open' ? (d.lotsNum ?? 0).toFixed(2) + ' lots' : '';
      break;
    case 'broker':
      t1 = a.broker;
      t2 = a.server;
      break;
    case 'vps':
      t1 = a.vps_id;
      t2 = a.terminal_id + (a.dup_terminal ? ' ×2' : '');
      ti = a.dup_terminal ? 'Also connected from a duplicate terminal' : '';
      break;
    case 'strat':
      t1 = a.strategy;
      t2 = a.tags.join(' · ');
      break;
    case 'bal':
      mono1 = true;
      color = (d.balNum ?? 0) < 0 ? C.rd : C.tx;
      t1 = fmtMoney(a.balance, a.currency);
      break;
    case 'eq':
      mono1 = true;
      color = (d.eqNum ?? 0) <= 0 ? C.rd : C.tx;
      t1 = fmtMoney(a.equity, a.currency);
      break;
    case 'fpl':
      mono1 = true;
      color = (d.fltNum ?? 0) >= 0 ? C.gn : C.rd;
      t1 = fmtMoney(a.floating_pl, a.currency, true);
      break;
    case 'dpl':
      mono1 = true;
      color = (d.dailyNum ?? 0) >= 0 ? C.gn : C.rd;
      t1 = fmtMoney(a.daily_pl, a.currency, true);
      t2 = d.dailyPctNum != null ? fmtPctStr(a.daily_pct, true) : '';
      break;
    case 'dd':
      mono1 = true;
      color = d.ddNum == null ? C.gy : d.ddNum > thr.dC ? C.rd : d.ddNum > thr.dW ? C.am : C.b9;
      t1 = fmtPctStr(a.drawdown_pct);
      ti = 'Drawdown from equity peak';
      break;
    case 'mlvl':
      mono1 = true;
      color = d.mlvlNum == null ? C.gy : d.mlvlNum < thr.mC ? C.rd : d.mlvlNum < thr.mW ? C.am : C.b9;
      t1 = d.mlvlNum == null ? 'Unavailable' : Math.round(d.mlvlNum) + '%';
      ti = 'Equity / used margin × 100';
      break;
    case 'fmg':
      mono1 = true;
      color = d.fmgNum == null ? C.gy : C.b9;
      t1 = d.fmgNum == null ? 'Unavailable' : fmtMoney(a.free_margin, a.currency);
      break;
    case 'pos':
      mono1 = true;
      color = a.positions_count > 0 ? C.bl : C.dim2;
      t1 = String(a.positions_count);
      break;
    case 'lots':
      mono1 = true;
      color = (d.lotsNum ?? 0) > 0 ? C.b9 : C.dim2;
      t1 = (d.lotsNum ?? 0).toFixed(2);
      break;
    case 'hb':
      mono1 = true;
      color = d.cm[1];
      t1 = fmtAge(d.hbAgeMs);
      ti = hbTitle(d.receivedAtMs, a.broker_tz_offset);
      break;
    case 'qt':
      mono1 = true;
      color = d.tr === 'inactive' ? C.gy : (d.qtAgeMs ?? 0) > 300000 ? C.rd : (d.qtAgeMs ?? 0) > 60000 ? C.am : C.b9;
      t1 = d.tr === 'inactive' ? '☾ closed' : fmtAge(d.qtAgeMs);
      ti = hbTitle(d.quoteAtMs, a.broker_tz_offset);
      break;
  }

  const isFrozen = col.id === 'acct' || col.id === 'conn';
  const flashing = flashTs != null && nowLocal - flashTs < 900;
  const bg = flashing
    ? isFrozen
      ? '#1B2A44'
      : 'rgba(76,158,232,.14)'
    : crit
      ? isFrozen
        ? '#1C1C2B'
        : 'rgba(228,91,91,.05)'
      : isFrozen
        ? C.s1
        : 'transparent';

  const td: CSSProperties = {
    padding: '3px 10px',
    height: rowH,
    borderBottom: '1px solid #16203A',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textAlign: col.al,
    verticalAlign: 'middle',
    transition: 'background .5s',
    background: bg,
  };
  if (isFrozen) {
    td.position = 'sticky';
    td.left = frozenLeft[col.id];
    td.zIndex = 2;
    if (col.id === 'conn') td.borderRight = '1px solid ' + C.ln;
  }

  return (
    <td style={td} title={ti || undefined}>
      <div style={{ fontSize: 12, lineHeight: '15px', overflow: 'hidden', textOverflow: 'ellipsis', color, fontWeight: bold ? 600 : 400, fontFamily: mono1 ? MONO : undefined, fontVariantNumeric: 'tabular-nums' }}>
        {t1}
      </div>
      {t2 !== '' && (
        <div style={{ fontSize: 10, lineHeight: '12px', color: C.dm, fontFamily: MONO, overflow: 'hidden', textOverflow: 'ellipsis' }}>{t2}</div>
      )}
    </td>
  );
}

// ---- Narrow-layout cards --------------------------------------------------------------

function Cards({ list, derivedMap, thr }: { list: AccountRow[]; derivedMap: Map<string, Derived>; thr: Thresholds }) {
  if (list.length === 0) {
    return (
      <div style={{ padding: '34px 16px', textAlign: 'center', background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>No accounts match</div>
        <div style={{ fontSize: 11.5, color: C.dm, marginTop: 5 }}>Clear filters or adjust the search to see the fleet.</div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {list.map((a) => {
        const d = derivedMap.get(a.id)!;
        const sv = (color?: string): CSSProperties => ({ fontFamily: MONO, fontSize: 12.5, fontWeight: 600, color: color ?? C.tx, fontVariantNumeric: 'tabular-nums', marginTop: 1 });
        const stats: Array<[string, string, CSSProperties]> = [
          ['Equity', fmtMoney(a.equity, a.currency), sv()],
          ['Float P&L', fmtMoney(a.floating_pl, a.currency, true), sv((d.fltNum ?? 0) >= 0 ? C.gn : C.rd)],
          ['Day', fmtMoney(a.daily_pl, a.currency, true), sv((d.dailyNum ?? 0) >= 0 ? C.gn : C.rd)],
          ['DD', fmtPctStr(a.drawdown_pct), sv(d.ddNum != null && d.ddNum > thr.dC ? C.rd : d.ddNum != null && d.ddNum > thr.dW ? C.am : undefined)],
          ['Margin lvl', d.mlvlNum == null ? 'Unavail.' : Math.round(d.mlvlNum) + '%', sv(d.mlvlNum != null && d.mlvlNum < thr.mC ? C.rd : d.mlvlNum != null && d.mlvlNum < thr.mW ? C.am : undefined)],
          ['Heartbeat', fmtAge(d.hbAgeMs), sv(d.cm[1])],
        ];
        return (
          <button
            key={a.id}
            onClick={() => openDrawer(a.id)}
            style={{ textAlign: 'left', background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, padding: 12, display: 'flex', flexDirection: 'column', gap: 8, color: C.tx, fontFamily: 'inherit', width: '100%' }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{a.label}</span>
              <span style={{ fontSize: 10.5, color: C.dm, fontFamily: MONO }}>{a.login_masked + ' · ' + a.broker + ' · ' + a.terminal_id}</span>
            </div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {[d.cm, d.bm, d.rm, d.tm].map((tok, i) => (
                <span key={i} style={chipS(tok[1])}>
                  {tok[0]} {tok[2]}
                </span>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '6px 10px' }}>
              {stats.map(([k, v, style]) => (
                <div key={k}>
                  <div style={{ fontSize: 9, color: C.dm, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>{k}</div>
                  <div style={style}>{v}</div>
                </div>
              ))}
            </div>
          </button>
        );
      })}
    </div>
  );
}
